import styles from './assetClassOverview.module.scss';
import shell from '../v2/pageShell.module.scss';
import {useMutation, useQuery} from '@apollo/client';
import {GET_ASSET_CLASSES} from 'graphql/queries/assetManagement';
import {Dropdown, Skeleton} from 'antd';
import {useNavigate} from 'react-router-dom';
import dotsIcon from 'images/dots.svg';
import {useState} from 'react';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {DELETE_ASSET_CLASS} from 'graphql/mutations/assetManagement';
import {handleDeleteAssetClass} from '../actions';
import {Button} from '@dabafinance/react-components';
import {NavTabsEnum, supportedLanguages} from 'utils/constants';
import {useSelector} from 'react-redux';
import {makeSelectTabOperations} from 'redux/store/auth';
import {assetClassLabel, instrumentKeysFor} from '../modules/instrumentKeys';
import {formatEnumType} from 'utils/helper';

const STATUS_CLASS = {
  ENABLED: shell.statusOk,
  PENDING: shell.statusWarn,
  DISABLED: shell.statusStop,
};

const languageLabel = code =>
  supportedLanguages.find(language => language.value === code)?.label ?? code;

/**
 * The key badge, humanised — and dropped when it only repeats the name.
 *
 * It was printing the raw enum, so a class called "FIXED INCOME" carried a
 * "FIXED_INCOMES" badge underneath it. `assetClassLabel` is the one place that
 * knows these read as "Fixed income", but applying it alone would leave the
 * card saying the same words twice in two cases. The badge is there to identify
 * a class whose stored name does not, so when the name already does the job it
 * has nothing to add.
 */
const compactKey = value =>
  (value || '')
    .toLowerCase()
    .replace(/[^a-z]/g, '')
    .replace(/s$/, '');

const badgeFor = assetClass => {
  const label = assetClassLabel(assetClass?.key);
  if (!label || label === '—') return null;
  return compactKey(label) === compactKey(assetClass?.name) ? null : label;
};

/**
 * Asset classes.
 *
 * Was a five-column table over three rows, where the only real interaction —
 * opening a class — was an undiscoverable click on the row itself. Cards make
 * that the obvious action and have room for what an operator actually wants
 * before clicking: what the class is, and which instrument types it holds.
 */
const AssetClassesOverview = () => {
  const [openDeleteModal, setOpenDeleteModal] = useState({
    isOpen: false,
    id: null,
  });
  const navigate = useNavigate();
  const {data, loading, refetch} = useQuery(GET_ASSET_CLASSES);
  const [deleteAssetClass, {loading: loadingDelete}] =
    useMutation(DELETE_ASSET_CLASS);
  const pageOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.assetClasses),
  );

  const classes = data?.getAssetClasses?.data ?? [];

  const openInstruments = assetClass =>
    navigate(
      `/dashboard/asset-management/class/${assetClass?.key}/instruments`,
    );

  const menuFor = assetClass => ({
    items: [
      {
        key: 0,
        label: 'View instruments',
        onClick: e => {
          e.domEvent.stopPropagation();
          openInstruments(assetClass);
        },
      },
      {
        key: 1,
        label: 'View language versions',
        onClick: e => {
          e.domEvent.stopPropagation();
          navigate(
            `/dashboard/asset-management/classes/languages/${assetClass?.id}`,
          );
        },
      },
      {
        key: 2,
        label: 'Delete class',
        disabled: !pageOperations?.delete,
        onClick: e => {
          e.domEvent.stopPropagation();
          setOpenDeleteModal({isOpen: true, id: assetClass?.id});
        },
      },
    ],
  });

  return (
    <div>
      <div className={styles.top}>
        <h1>Asset Classes</h1>
        <div>
          <Button
            onClick={() =>
              navigate(`/dashboard/asset-management/classes/create`)
            }
            label="Add Asset Class"
            disabled={!pageOperations?.create}
          />

          <Button
            onClick={() =>
              navigate(`/dashboard/asset-management/classes/ratings`)
            }
            label="Ratings"
            type="secondary"
          />
        </div>
      </div>

      {loading ? (
        <div className={shell.classGrid}>
          {[0, 1, 2].map(i => (
            <div key={i} className={shell.classCard}>
              <Skeleton active paragraph={{rows: 3}} />
            </div>
          ))}
        </div>
      ) : classes.length ? (
        <div className={shell.classGrid}>
          {classes.map(assetClass => {
            const types = instrumentKeysFor(assetClass?.key);
            return (
              <div
                key={assetClass?.id}
                role="button"
                tabIndex={0}
                className={shell.classCard}
                onClick={() => openInstruments(assetClass)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openInstruments(assetClass);
                  }
                }}>
                <div className={shell.classHead}>
                  <div>
                    <div className={shell.className}>{assetClass?.name}</div>
                    {badgeFor(assetClass) && (
                      <span className={shell.classKey}>
                        {badgeFor(assetClass)}
                      </span>
                    )}
                  </div>
                  <Dropdown trigger="click" menu={menuFor(assetClass)}>
                    <div
                      role="button"
                      className={shell.classMenu}
                      onClick={e => e.stopPropagation()}>
                      <img src={dotsIcon} alt="Actions" />
                    </div>
                  </Dropdown>
                </div>

                {assetClass?.description && (
                  <p className={shell.classDescription}>
                    {assetClass.description}
                  </p>
                )}

                <div className={shell.classTypes}>
                  {types.map(type => (
                    <span key={type.key} className={shell.typeChip}>
                      {type.label}
                    </span>
                  ))}
                </div>

                <div className={shell.classFoot}>
                  <span
                    className={`${shell.status} ${
                      STATUS_CLASS[assetClass?.status] ?? shell.statusWarn
                    }`}>
                    {formatEnumType(assetClass?.status)}
                  </span>
                  <span className={shell.classMeta}>
                    {languageLabel(assetClass?.language)}
                  </span>
                  <span className={shell.classGo}>View instruments →</span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className={`${shell.surface} ${shell.empty}`}>
          <h3>No asset classes yet</h3>
          <p>
            Create one to start grouping the instruments investors can hold.
          </p>
        </div>
      )}

      <ConfirmModal
        openModal={openDeleteModal.isOpen}
        title="Delete Asset Class"
        confirmText="Are you sure you want to delete this Asset Class? this action cannot be undone."
        setOpenModal={() => setOpenDeleteModal({isOpen: false, id: null})}
        loading={loadingDelete}
        handleOperation={() =>
          handleDeleteAssetClass(
            deleteAssetClass,
            openDeleteModal.id,
            refetch,
            setOpenDeleteModal,
          )
        }
      />
    </div>
  );
};

export default AssetClassesOverview;
